import { useEffect, useState, useRef, useCallback } from 'react';
import { FaEnvelope, FaPhone, FaMapMarkerAlt } from 'react-icons/fa';
import './About.css';

const CounterAnimation = ({ target, suffix, duration, isVisible }) => {
  const [count, setCount] = useState(0);
  const hasStarted = useRef(false);

  const animate = useCallback((startTime, currentTime) => {
    if (!startTime) startTime = currentTime;
    const progress = Math.min((currentTime - startTime) / duration, 1);
    const easeOutQuart = 1 - Math.pow(1 - progress, 4);
    setCount(Math.floor(easeOutQuart * target));
    if (progress < 1) {
      requestAnimationFrame((time) => animate(startTime, time));
    }
  }, [target, duration]);

  useEffect(() => {
    if (isVisible && !hasStarted.current) {
      hasStarted.current = true;
      requestAnimationFrame((time) => animate(null, time));
    }
  }, [isVisible, animate]);

  return <span>{count}{suffix}</span>;
};

const About = () => {
  const [profileData, setProfileData] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const statsRef = useRef(null);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/api/profile/`)
      .then(res => res.json())
      .then(data => setProfileData(data))
      .catch(err => console.error('Error fetching profile:', err));
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.3 }
    );
    const el = statsRef.current;
    if (el) observer.observe(el);
    return () => { if (el) observer.unobserve(el); };
  }, []);

  const stats = profileData ? [
    { label: 'Years Experience',   target: profileData.years_experience,  suffix: '+' },
    { label: 'Projects Completed', target: profileData.projects_completed, suffix: '+' },
    { label: 'Technologies',       target: profileData.technologies_count, suffix: '+' },
    { label: 'Client Satisfaction',target: profileData.satisfaction,       suffix: '%' },
  ] : [];

  return (
    <section id="about" className="section about" aria-label="About section">
      <div className="container">
        <h2 className="section-title">About Me</h2>

        <div className="about-content">
          <div className="about-image">
            <div className="image-wrapper">
              <img
                src={profileData?.profile_image || 'https://placehold.co/400x500/1a1a1a/ffffff?text=Profile'}
                alt={profileData?.name
                  ? `${profileData.name} - ${profileData.title}`
                  : 'Pariwartan Sharma - Full Stack Developer'}
                className="about-img"
                loading="lazy"
                width="400"
                height="500"
              />
              <div className="image-overlay"></div>
            </div>
          </div>

          <div className="about-text">
            {/* Render separate paragraphs if set, otherwise fall back to bio */}
            {(profileData?.bio_paragraph_1 || profileData?.bio_paragraph_2 || profileData?.bio_paragraph_3)
              ? (
                <>
                  {profileData.bio_paragraph_1 && <p>{profileData.bio_paragraph_1}</p>}
                  {profileData.bio_paragraph_2 && <p>{profileData.bio_paragraph_2}</p>}
                  {profileData.bio_paragraph_3 && <p>{profileData.bio_paragraph_3}</p>}
                </>
              )
              : <p>{profileData?.bio || 'Loading...'}</p>
            }

            <div className="contact-info">
              {profileData?.location && (
                <div className="contact-item">
                  <FaMapMarkerAlt className="contact-icon" />
                  <span>{profileData.location}</span>
                </div>
              )}
              {profileData?.phone && (
                <div className="contact-item">
                  <FaPhone className="contact-icon" />
                  <a href={`tel:${profileData.phone}`}>{profileData.phone}</a>
                </div>
              )}
              {profileData?.email && (
                <div className="contact-item">
                  <FaEnvelope className="contact-icon" />
                  <a href={`mailto:${profileData.email}`}>{profileData.email}</a>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="stats-container" ref={statsRef}>
          {stats.map((stat, i) => (
            <div key={i} className="stat-item">
              <h3 className="stat-number">
                <CounterAnimation
                  target={stat.target}
                  suffix={stat.suffix}
                  duration={2000 + i * 200}
                  isVisible={isVisible}
                />
              </h3>
              <p className="stat-label">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default About;
